import {
    AlertToastType,
    ButtonFlavor,
    ButtonType,
    ExButton,
    ExLoader,
    ExPageHeader,
    ToastController,
} from '@boomi/exosphere';
import { useEffect, useState } from 'react';
import { type Connector, getConnector } from '../connectors/connectors';
import {
    fetchIdentityProviders,
    type IdentityProvider,
} from '../identity-providers/identityProviders';
import { trackCreateConfig } from '../pendo';
import { GENERIC_ERROR_MESSAGE } from '../shared/apiUtils';
import ErrorAlertBanner from '../shared/ErrorAlertBanner';
import styles from '../shared/Form.module.css';
import connectionStyles from './Connection.module.css';
import ConnectionFormFields, {
    EMPTY_FORM_VALUES,
    type FormValues,
    isFormComplete,
} from './ConnectionFormFields';
import ConnectorSidebar from './ConnectorSidebar';
import { type CreatedConnection, createConnection } from './connections';

interface ConnectionCreateProps {
    connectorId: string;
    onCreate: (connection: CreatedConnection) => void;
    onClose: () => void;
    onCancel: () => void;
    onBack?: (() => void) | undefined;
}

const ConnectionCreate = ({ connectorId, onCreate, onCancel, onBack }: ConnectionCreateProps) => {
    const [values, setValues] = useState<FormValues>(EMPTY_FORM_VALUES);
    const [useOwnCredentials, setUseOwnCredentials] = useState(false);
    const [connector, setConnector] = useState<Connector | null>(null);
    const [identityProviders, setIdentityProviders] = useState<IdentityProvider[]>([]);
    const [isLoadingIdps, setIsLoadingIdps] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [toolCount, setToolCount] = useState(0);

    useEffect(() => {
        Promise.all([fetchIdentityProviders(), getConnector(connectorId)])
            .then(([idpResponse, connectorData]) => {
                setIdentityProviders(idpResponse.results);
                setConnector(connectorData);
            })
            .finally(() => setIsLoadingIdps(false));
    }, [connectorId]);

    const handleChange = (patch: Partial<FormValues>) =>
        setValues((prev) => ({ ...prev, ...patch }));

    const handleSubmit = async (e?: React.FormEvent) => {
        e?.preventDefault();
        setHasSubmitted(true);
        if (!connector || !isFormComplete(values, connector, { useOwnCredentials })) return;
        setError(null);
        setSubmitting(true);

        const accountOwned = !connector.boomiSuppliesAppAuthOptions || useOwnCredentials;

        try {
            const createdConnection = await createConnection({
                name: values.name,
                identityProviderId: values.identityProviderId || null,
                connectorId,
                options: values.userOptions,
                enabled: true,
                accountOwned,
                disabledTools: values.disabledTools,
            });
            trackCreateConfig({
                connector_type: accountOwned ? 'Manual-configured' : 'Pre-configured',
                config_name: values.name,
                auth_method: connector.authorizationStrategy,
                total_tools_selected: toolCount - values.disabledTools.length,
                status: 'Success',
            });
            ToastController.show({
                description: `${values.name} created successfully.`,
                type: AlertToastType.SUCCESS,
            });
            onCreate(createdConnection);
        } catch (err) {
            trackCreateConfig({
                connector_type: accountOwned ? 'Manual-configured' : 'Pre-configured',
                config_name: values.name,
                auth_method: connector.authorizationStrategy,
                total_tools_selected: toolCount - values.disabledTools.length,
                status: 'Failed',
            });
            setError(err instanceof Error ? err.message : GENERIC_ERROR_MESSAGE);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            {connector ? (
                <div className={connectionStyles.drawerLayout}>
                    <ConnectorSidebar connector={connector} onBack={onBack} />
                    <div className={connectionStyles.drawerMainContent}>
                        <div className={connectionStyles.formColumn}>
                            <ExPageHeader
                                className={connectionStyles.header}
                                pageTitle="Create Config"
                            />
                            {error && (
                                <ErrorAlertBanner
                                    className={styles.saveError}
                                    onClose={() => setError(null)}
                                >
                                    {error}
                                </ErrorAlertBanner>
                            )}
                            <form className={styles.form} onSubmit={handleSubmit}>
                                <ConnectionFormFields
                                    values={values}
                                    identityProviders={identityProviders}
                                    isLoadingIdentityProviders={isLoadingIdps}
                                    onChange={handleChange}
                                    userAuthOptions={connector.userAuthOptions}
                                    appAuthOptions={connector.appAuthOptions}
                                    boomiSuppliesAppAuthOptions={
                                        connector.boomiSuppliesAppAuthOptions
                                    }
                                    scopes={connector.scopes}
                                    useOwnCredentials={useOwnCredentials}
                                    onUseOwnCredentialsChange={setUseOwnCredentials}
                                    connectorName={connector.name}
                                    connectorId={connectorId}
                                    hasSubmitted={hasSubmitted}
                                    onToolCountChange={setToolCount}
                                />
                            </form>
                        </div>
                        <div className={`${styles.actions} ${styles.footerActions}`} slot="footer">
                            <ExButton
                                data-pendo-button="home-createconfig-cancel"
                                type={ButtonType.SECONDARY}
                                flavor={ButtonFlavor.BRANDED}
                                onClick={onCancel}
                            >
                                Cancel
                            </ExButton>
                            <ExButton
                                data-pendo-button="home-createconfig-save"
                                type={ButtonType.PRIMARY}
                                flavor={ButtonFlavor.BRANDED}
                                disabled={
                                    submitting ||
                                    !!error ||
                                    (hasSubmitted &&
                                        !!connector &&
                                        !isFormComplete(values, connector, { useOwnCredentials }))
                                }
                                onClick={() => handleSubmit()}
                            >
                                {submitting ? 'Saving…' : 'Save'}
                            </ExButton>
                        </div>
                    </div>
                </div>
            ) : (
                <ExLoader />
            )}
        </>
    );
};

export default ConnectionCreate;
